import client from "@/api/client"
import PasswordInput from "@/components/Inputs/PasswordInput"
import ProfilePictureUpload from "@/components/Inputs/ProfilePictureUpload"
import useAppToast from "@/hooks/useAppToast"
import useFileManager from "@/hooks/useFileManager"
import { STRAPI_ERRORS } from "@/utils/errors"
import {
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react"
import { GetServerSidePropsContext } from "next"
import { unstable_getServerSession } from "next-auth"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import Link from "next/link"
import { useRouter } from "next/router"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { authOptions } from "../../api/auth/[...nextauth]"

interface IFormData {
  first_name: string
  last_name: string
  username: string
  email: string
  password: string
  cgu_consent: boolean
  newsletter_consent: boolean
  pictureFile?: File
  pictureUrl?: string
  picture?: string
  notifications_consent: boolean
  mobile_notifications_consent: boolean
}

const Register = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const toast = useAppToast()

  const methods = useForm<IFormData>({
    defaultValues: { email: router.query.email as string },
  })
  const { register, handleSubmit, formState } = methods
  const { uploadFile, isUploading } = useFileManager()

  const { mutate: handleRegister } = useMutation(
    "register",
    (data: IFormData) => client.auth.localRegisterCreate(data),
    {
      onSuccess: (_, variables) => {
        const { pictureUrl, first_name, last_name } = variables
        router.push(
          `/auth/register/confirmation?pictureUrl=${pictureUrl}&userName=${first_name}_${last_name}`
        )
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error?.message

        if (errorMessage === STRAPI_ERRORS.EMAIL_ALREADY_USED) {
          toast({ title: t("auth.user_exists_error"), status: "error" })
        }

        if (errorMessage === STRAPI_ERRORS.INVALID_PASSWORD) {
          toast({ title: t("auth.invalid_password_error"), status: "error" })
        }
      },
    }
  )

  const onSubmit = async (data: IFormData) => {
    const dataToSend = { ...data }

    dataToSend.email = dataToSend.email.trim().toLowerCase()
    dataToSend.username = dataToSend.email.trim().toLowerCase()
    dataToSend.first_name = dataToSend.first_name.trim().toLowerCase()
    dataToSend.last_name = dataToSend.last_name.trim().toLowerCase()

    if (data?.pictureFile) {
      await uploadFile(data?.pictureFile).then((data) => {
        dataToSend.picture = data?.id
        dataToSend.pictureUrl = data?.url
      })
    }
    handleRegister(dataToSend)
  }

  return (
    <Center bg="primaryColor" minH="100%" fontFamily="GTMaru">
      <Stack spacing={4} width="100%" mx="auto" maxW="xl" py={12} px={6}>
        <Stack
          textAlign="center"
          align="center"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          flexDirection={{ base: "column", sm: "row" }}
        >
          <Text as="h2" fontSize="4xl" fontWeight="light">
            {t("auth.register_caption")}
          </Text>
          <HStack spacing={0}>
            <Text fontFamily="GTMaru" fontWeight="bold" fontSize="3xl">
              AMA
            </Text>
            <Text
              fontFamily="GTAlpina"
              fontSize="4xl"
              fontStyle="italic"
              paddingRight={1}
            >
              libri
            </Text>
            <Text fontWeight="light" as="h2" fontSize="4xl">
              !
            </Text>
          </HStack>
        </Stack>
        <Box rounded="lg" bg="white" boxShadow="lg" p={8}>
          <FormProvider {...methods}>
            <Stack as="form" onSubmit={handleSubmit(onSubmit)} spacing={4}>
              <ProfilePictureUpload name="picture" />
              <FormControl id="first_name">
                <Input
                  required
                  {...register("first_name")}
                  placeholder={t("auth.first_name_label")}
                  _placeholder={{
                    base: { fontSize: "sm" },
                    sm: { fontSize: "md" },
                  }}
                />
              </FormControl>
              <FormControl id="last_name">
                <Input
                  required
                  {...register("last_name")}
                  placeholder={t("auth.last_name_label")}
                  _placeholder={{
                    base: { fontSize: "sm" },
                    sm: { fontSize: "md" },
                  }}
                />
              </FormControl>
              <FormControl id="email">
                <Input
                  required
                  autoCapitalize="off"
                  autoCorrect="off"
                  {...register("email")}
                  placeholder={t("auth.email_label")}
                  _placeholder={{
                    base: { fontSize: "sm" },
                    sm: { fontSize: "md" },
                  }}
                  type="email"
                />
              </FormControl>
              <FormControl id="password">
                <PasswordInput name="password" required />
              </FormControl>
              <Checkbox
                size="sm"
                colorScheme="gray"
                required
                {...register("cgu_consent")}
              >
                <HStack flexWrap="wrap" spacing={0}>
                  <Text fontSize="sm" paddingRight={1}>
                    {t("auth.accept")}{" "}
                  </Text>
                  <Text textDecoration="underline" fontSize="sm">
                    <Link href="/terms/cgu">{t("auth.cgu_consent")}</Link>
                  </Text>
                </HStack>
              </Checkbox>
              <Checkbox
                size="sm"
                colorScheme="gray"
                {...register("newsletter_consent")}
              >
                {t("auth.newsletter_consent")}
              </Checkbox>
              <Checkbox
                size="sm"
                colorScheme="gray"
                {...register("notifications_consent")}
              >
                {t("auth.notifications_consent")}
              </Checkbox>
              <Stack spacing={10} paddingTop={6}>
                <Button
                  isLoading={formState.isSubmitting || isUploading}
                  type="submit"
                  variant="brand"
                >
                  {t("auth.register_label")}
                </Button>
              </Stack>
              <HStack flexWrap="wrap" spacing={0}>
                <Text fontSize="sm" paddingRight={1}>
                  {t("home.already_registered")}
                </Text>
                <Text textDecoration="underline" display="inline" fontSize="sm">
                  <Link href="/auth/login">{t("home.login")}</Link>
                </Text>
              </HStack>
            </Stack>
          </FormProvider>
        </Box>
      </Stack>
    </Center>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const session = await unstable_getServerSession(
    context.req,
    context.res,
    authOptions
  )
  if (session) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(context.locale!)),
    },
  }
}

export default Register
